import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Briefcase, CheckCircle2, FileText, MessageSquare, ShieldCheck } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { CTABand } from "@/components/CTABand";
import { ServiceCard } from "@/components/ServiceCard";
import { ProcessSteps } from "@/components/ProcessSteps";
import { FaqAccordion } from "@/components/FaqAccordion";
import { CONTACT, HERO_IMAGE, SERVICES, WHY_US, FAQ_ITEMS } from "@/data/content";

const fade = (delay) => ({
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay, ease: "easeOut" },
});

const WHY_ICONS = [FileText, MessageSquare, CheckCircle2, ShieldCheck, Briefcase];

const Hero = () => (
  <section data-testid="home-hero" className="relative overflow-hidden border-b border-slate-200 bg-cream">
    <div className="grid-bg grid-fade absolute inset-0" aria-hidden />
    <div className="relative mx-auto grid max-w-7xl gap-12 px-4 pb-20 pt-16 sm:px-6 md:pb-28 md:pt-24 lg:grid-cols-12 lg:items-center">
      <div className="lg:col-span-7">
        <motion.p {...fade(0)} data-testid="hero-overline" className="eyebrow">{CONTACT.name}</motion.p>
        <motion.h1 {...fade(0.08)} data-testid="hero-title" className="mt-4 font-display text-4xl font-bold leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
          Business Management &amp; <span className="text-brand">Website Development</span> Services
        </motion.h1>
        <motion.p {...fade(0.16)} data-testid="hero-subtitle" className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">
          My Geeks LLC helps small and mid-sized businesses improve their digital presence and day-to-day operations
          through consulting and website development — scoped in writing, priced by quotation, delivered in plain English.
        </motion.p>
        <motion.p {...fade(0.2)} data-testid="hero-b2b-statement" className="mt-4 max-w-2xl rounded-lg border border-slate-200 bg-white/70 px-4 py-3 text-sm leading-relaxed text-slate-700">
          We provide exclusive B2B consulting and custom software development; we do not offer remote IT support, computer repair, or consumer technical assistance.
        </motion.p>
        <motion.div {...fade(0.24)} className="mt-9 flex flex-col gap-3 sm:flex-row">
          <Link to="/contact" data-testid="hero-quote-button" className="btn-primary">
            Request a Quote <ArrowRight size={16} />
          </Link>
          <Link to="/services" data-testid="hero-services-button" className="btn-outline">View Services</Link>
        </motion.div>
        <motion.ul {...fade(0.32)} className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600">
          {["Written scope before work begins", "No guaranteed-ranking claims", "B2B clients only, billed by milestone"].map((t) => (
            <li key={t} className="flex items-center gap-2"><CheckCircle2 size={16} className="text-forest" />{t}</li>
          ))}
        </motion.ul>
      </div>
      <motion.div {...fade(0.2)} className="lg:col-span-5">
        <div className="card overflow-hidden">
          <img src={HERO_IMAGE} alt="Business consultants working together at a modern office" className="h-64 w-full object-cover md:h-80" />
          <div className="grid grid-cols-2 divide-x divide-slate-200 border-t border-slate-200">
            {[["Response", "Within 5 hours"], ["Hours", "Mon–Fri, 9–6 CST"]].map(([k, v]) => (
              <div key={k} className="p-4">
                <p className="label-xs">{k}</p>
                <p className="mt-1 text-sm font-semibold text-ink">{v}</p>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

const SectionHeading = ({ overline, title, text, link, linkLabel, testId }) => (
  <Reveal>
    <div className="mb-12 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
      <div>
        <p className="eyebrow">{overline}</p>
        <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">{title}</h2>
      </div>
      <div className="flex max-w-sm flex-col gap-3">
        {text && <p className="text-sm leading-relaxed text-slate-600">{text}</p>}
        {link && (
          <Link to={link} data-testid={testId} className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:underline">
            {linkLabel} <ArrowRight size={14} />
          </Link>
        )}
      </div>
    </div>
  </Reveal>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Seo
        title="Business Management & Website Development Services"
        description="My Geeks LLC provides business management consulting and website development services for small and mid-sized businesses. Request a written quote today."
      />
      <Hero />

      <section data-testid="home-services-section" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24">
        <SectionHeading overline="What we do" title="Services overview" text="Five clearly defined services. Every one is scoped in writing and priced by quotation." link="/services" linkLabel="All services" testId="home-all-services-link" />
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => <ServiceCard key={s.slug} service={s} index={i} compact />)}
        </div>
      </section>

      <section data-testid="home-why-section" className="border-y border-slate-200 bg-cream">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24">
          <SectionHeading overline="Why choose us" title="How we do business" text="No badges, no invented numbers — just the way we work." />
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {WHY_US.map((w, i) => {
              const Icon = WHY_ICONS[i];
              return (
                <Reveal key={w.title} delay={i * 0.06} className="h-full">
                  <div data-testid={`why-us-item-${i + 1}`} className="card card-hover flex h-full flex-col p-7">
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-brand-soft text-brand"><Icon size={20} /></span>
                    <h3 className="mt-5 font-display text-lg font-semibold tracking-tight">{w.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{w.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section data-testid="home-process-section" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-24">
        <SectionHeading overline="Our process" title="From enquiry to handover" link="/process" linkLabel="Full process" testId="home-process-link" />
        <ProcessSteps compact />
      </section>

      <section data-testid="home-faq-section" className="border-t border-slate-200 bg-cream">
        <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 md:py-24">
          <SectionHeading overline="Frequently asked" title="Common questions" link="/faq" linkLabel="All questions" testId="home-faq-link" />
          <FaqAccordion items={FAQ_ITEMS.slice(0, 4)} />
        </div>
      </section>

      <CTABand title="Tell us what your business needs." text="Request a written quotation, or call during business hours — a real person answers." />
    </div>
  );
}
